import { AdjustedGrade, Grade, GradingSection, SectionGradeUpdate } from "../types/inspection";

export const GRADE_TYPE_CONDITION = 1;
export const GRADE_TYPE_MANAGEMENT = 2;

/** Section display order keyed by vesselGradeId, from GET /grading-sections. */
export type SectionOrder = Map<number, number>;

/**
 * Build the fallback order lookup from a grading-sections payload. Rows without
 * both ids are skipped rather than defaulted, so a partial template narrows what
 * the fallback can answer instead of inventing positions.
 */
export const sectionOrderFromSections = (sections: GradingSection[] | null): SectionOrder => {
    const order: SectionOrder = new Map();

    (sections ?? []).forEach((section) => {
        if (Number.isFinite(section?.vesselGradeId) && Number.isFinite(section?.order)) {
            order.set(section.vesselGradeId, section.order);
        }
    });

    return order;
};

/**
 * The display position for one grade: the payload's own `order` when it has one,
 * otherwise the grading-sections template, otherwise nothing.
 */
const displayOrder = (grade: Grade, sectionOrder?: SectionOrder): number | undefined => {
    if (Number.isFinite(grade.order)) {
        return grade.order;
    }

    const fromTemplate = sectionOrder?.get(grade.vesselGradeId);

    return Number.isFinite(fromTemplate) ? fromTemplate : undefined;
};

/**
 * Order grading areas for display.
 *
 * Sorting on `order` alone is unsafe: the get-by-id payload has not always
 * carried the field, and `a.order - b.order` over a missing one is NaN, which
 * sort treats as 0 — so the rows keep payload order and an unsorted page is
 * indistinguishable from a sorted one. Fall back to the grading-sections
 * template (what the Grading page orders by), and where neither source knows,
 * keep the payload's relative order rather than interleaving guesses.
 *
 * Returns a new array; the caller's is not mutated.
 */
export const sortGradesForDisplay = (grades: Grade[], sectionOrder?: SectionOrder): Grade[] => {
    const positioned = grades.map((grade, index) => ({
        grade,
        index,
        order: displayOrder(grade, sectionOrder),
    }));

    const unordered = positioned.filter((entry) => entry.order === undefined);
    if (unordered.length) {
        console.warn(
            "Grading areas with no display order; falling back to payload order for: " +
                unordered.map((entry) => entry.grade.name).join(", ")
        );
    }

    return positioned
        .sort((one, two) => {
            // Unplaceable rows sort last, in the order they arrived.
            if (one.order === undefined || two.order === undefined) {
                if (one.order === two.order) {
                    return one.index - two.index;
                }
                return one.order === undefined ? 1 : -1;
            }

            return one.order === two.order ? one.index - two.index : one.order - two.order;
        })
        .map((entry) => entry.grade);
};

const calculateManagementGrade = (grades: Grade[]) => {
    let totalManagement = 0;
    let maxManagement = 0;
    grades.filter(grade => grade.type === GRADE_TYPE_MANAGEMENT && grade.point !== null).forEach(grade => {
        const gradePoint = grade.adjustedPoint ?? grade.point;
        totalManagement += (gradePoint / 2) * grade.weight;
        maxManagement += 5 * grade.weight;
    });

    return {
        totalManagementGrade: totalManagement,
        maxManagementGrade: maxManagement,
        computedManagementGrade: Math.round((totalManagement / maxManagement) * 100)
    }
}

const calculateConditionGrade = (grades: Grade[]) => {
    let totalCondition = 0;
    let maxCondition = 0;

    grades.filter(grade => grade.type === GRADE_TYPE_CONDITION && grade.point !== null).forEach(grade => {
        const gradePoint = grade.adjustedPoint ?? grade.point;
        totalCondition += (gradePoint / 2) * grade.weight;
        maxCondition += 5 * grade.weight;
    });

    return {
        totalConditionGrade: totalCondition,
        maxConditionGrade: maxCondition,
        computedConditionGrade: Math.round((totalCondition / maxCondition) * 100)
    }
}

export const calculateGrades = (grades: Grade[]) => {
    const managementGrade = calculateManagementGrade(grades);
    const conditionGrade = calculateConditionGrade(grades);

    const totalGrade = managementGrade.totalManagementGrade + conditionGrade.totalConditionGrade;
    const maxGrade = managementGrade.maxManagementGrade + conditionGrade.maxConditionGrade;

    const overallGrade = Math.round(((totalGrade / maxGrade) * 100));

    return {
        managementGrade,
        conditionGrade,
        overallGrade
    }
}

/**
 * The three summary grades shown at the top of the Grading page.
 * `overallGrade` is the Idwal Grade. Each value is a computed percentage
 * (0–100), or `null` when there is nothing graded to compute it from
 * (blank/unset) — never `NaN`.
 */
export type SectionGradeSummary = {
    overallGrade: number | null;
    conditionGrade: number | null;
    managementGrade: number | null;
};

/** The minimal fields the summary calc needs. A full `Grade` satisfies this,
 *  as does a lightweight object built from the Grading page's draft state. */
export type SectionGradeInput = {
    type: number;
    weight: number;
    point: number | null;
};

/** Weighted total and max for the graded sections of one grade type. Ungraded
 *  sections (point null/undefined) are excluded from BOTH sums, so their weight
 *  never inflates the denominator. */
const weightedTotalsForType = (grades: SectionGradeInput[], type: number) => {
    let total = 0;
    let max = 0;
    grades.forEach(grade => {
        const point = grade.point;
        if (grade.type !== type || point === null || point === undefined) {
            return;
        }
        total += (point / 2) * grade.weight;
        max += 5 * grade.weight;
    });
    return { total, max };
};

/** Percentage from a weighted total/max, or `null` when nothing was graded
 *  (max === 0) so the caller renders blank instead of `NaN`. */
const computedGrade = (total: number, max: number): number | null =>
    max > 0 ? Math.round((total / max) * 100) : null;

/**
 * Real-time summary grades for the tech-reviewer Grading page.
 *
 * Operates on stored-scale points (0–10, matching `Grade.point`); callers
 * convert the 0–100 UI value to a point (÷10) before calling. Ungraded
 * sections (`point` null/undefined) are excluded from both the weighted
 * numerator and the max denominator. When a grade type has no graded
 * sections its computed grade is `null` (blank/unset), never `NaN`; when
 * nothing at all is graded the overall (Idwal) grade is `null` too.
 *
 * Distinct from `calculateGrades`, which is used by the Adjusted Grade
 * Editor and returns `NaN` for the no-sections-graded case.
 */
export const calculateSectionGrades = (grades: SectionGradeInput[]): SectionGradeSummary => {
    const condition = weightedTotalsForType(grades, GRADE_TYPE_CONDITION);
    const management = weightedTotalsForType(grades, GRADE_TYPE_MANAGEMENT);

    return {
        conditionGrade: computedGrade(condition.total, condition.max),
        managementGrade: computedGrade(management.total, management.max),
        overallGrade: computedGrade(condition.total + management.total, condition.max + management.max),
    };
};

/** A grading-page draft row, reduced to what the save payload needs. */
export type SectionGradeDraft = {
    vesselGradeId: number;
    /** 0–100 UI value, or null when ungraded. */
    uiGrade: number | null;
};

/**
 * Build the save payload from the page's draft rows. Every section is included
 * — an ungraded section is sent with `point: null` so that clearing a
 * previously-saved grade persists as ungraded. The UI 0–100 value is converted
 * to the stored 0–10 `point` scale.
 */
export const sectionGradesDto = (draft: SectionGradeDraft[]): SectionGradeUpdate[] =>
    draft.map((row) => ({
        vesselGradeId: row.vesselGradeId,
        point: row.uiGrade === null ? null : row.uiGrade / 10,
    }));

export const combineAdjustedGrades = (inspectionVesselGrades: Grade[], adjustedGrades: AdjustedGrade[]) => {
    if (!adjustedGrades || adjustedGrades.length === 0) {
        return inspectionVesselGrades;
    }

    const combinedGrades: Grade[] = [];
    inspectionVesselGrades.forEach(grade => {
        const adjustedGrade = adjustedGrades.find(adjustedGrade => adjustedGrade.vesselGradeId === grade.vesselGradeId);

        if (grade.point !== null && grade.point !== undefined && adjustedGrade && adjustedGrade.gradeValue !== null && adjustedGrade.gradeValue !== undefined) {
            combinedGrades.push({
                ...grade,
                adjustedPoint: adjustedGrade.gradeValue / 10
            });
        } else if (grade.point !== null && grade.point !== undefined) {
            combinedGrades.push(grade);
        }
    });

    return combinedGrades;
}

export const adjustedGradesDto = (adjustedGrades: Grade[]) => {
    return adjustedGrades.filter(grade => grade.point !== null).map(grade => {
        return {
            vesselGradeId: grade.vesselGradeId,
            gradeValue: grade.adjustedPoint !== undefined ? grade.adjustedPoint * 10 : grade.point * 10,
            point: grade.point,
            adjustedPoint: grade.adjustedPoint
        }
    });
}